import React, { useState, useEffect, useCallback } from 'react';
import { getActivityLogs, getActivityLogDetail } from '../services/systemService';
import { formatDateTime } from '../utils/dateFormat';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import '../css/SystemManagement.css';

const SystemManagement = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalElements: 0, totalPages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getActivityLogs({
        page: pagination.page,
        size: pagination.size,
        search: searchKeyword,
        method: methodFilter,
        success: successFilter,
      });
      setLogs(data.content || []);
      setPagination((prev) => ({
        ...prev,
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
      }));
    } catch (error) {
      showSnackbar(error.message || '활동 로그를 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, searchKeyword, methodFilter, successFilter, showSnackbar]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSearchKeyword(searchInput.trim());
  };

  const handlePage = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const openDetail = async (logId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const result = await getActivityLogDetail(logId);
      setDetail(result?.data || null);
    } catch (error) {
      showSnackbar(error.message || '상세 정보를 불러올 수 없습니다.', 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const methodBadgeClass = (m) => {
    const map = { POST: 'badge-post', PATCH: 'badge-patch', PUT: 'badge-put', DELETE: 'badge-delete' };
    return `badge ${map[m] || 'badge-default'}`;
  };

  return (
    <div className="admin-layout">
      <Header />
      <PageHeader title="시스템 / 보안 관리" />
      <div className="system-management">
        <div className="main-content">
          <p className="page-subtitle">접속자 / 운영자 활동 내역을 확인할 수 있습니다 (수정 불가)</p>

          <div className="log-toolbar">
            <input
              type="text"
              className="search-input"
              placeholder="이메일, 엔드포인트로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <select value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}>
              <option value="">전체 메서드</option>
              <option value="POST">POST</option>
              <option value="PATCH">PATCH</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
            <select value={successFilter} onChange={(e) => { setSuccessFilter(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}>
              <option value="">전체 상태</option>
              <option value="true">성공</option>
              <option value="false">실패</option>
            </select>
            <button className="btn-search" onClick={handleSearch}>검색</button>
            <div className="toolbar-spacer" />
            <span className="total-count">총 {pagination.totalElements}개</span>
          </div>

          <div className="log-table-wrap">
            <table className="log-table">
              <thead>
                <tr>
                  <th>접속 시각</th>
                  <th>사용자</th>
                  <th>메서드</th>
                  <th>엔드포인트</th>
                  <th>응답 코드</th>
                  <th>IP</th>
                  <th>처리 시간</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="empty-row">불러오는 중...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={7} className="empty-row">로그가 없습니다</td></tr>
                ) : (
                  logs.map((l) => (
                    <tr key={l.id} onClick={() => openDetail(l.id)} className="clickable-row">
                      <td>{formatDateTime(l.createdAt)}</td>
                      <td>{l.adminEmail || '(미인증)'}</td>
                      <td><span className={methodBadgeClass(l.method)}>{l.method}</span></td>
                      <td className="endpoint-cell">{l.endpoint}</td>
                      <td>
                        <span className={`badge ${l.success ? 'badge-success' : 'badge-failed'}`}>
                          {l.responseStatus ?? '-'}
                        </span>
                      </td>
                      <td className="mono">{l.requestIp || '-'}</td>
                      <td>{l.durationMs ?? 0}ms</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => handlePage(pagination.page - 1)} disabled={pagination.page === 0}>이전</button>
              <span>{pagination.page + 1} / {pagination.totalPages}</span>
              <button onClick={() => handlePage(pagination.page + 1)} disabled={pagination.page + 1 >= pagination.totalPages}>다음</button>
            </div>
          )}
        </div>
      </div>

      {detailOpen && (
        <div className="modal-overlay" onClick={() => setDetailOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>로그 상세</h2>
              <button className="modal-close" onClick={() => setDetailOpen(false)}>×</button>
            </div>
            <div className="detail-body">
              {detailLoading ? (
                <p className="detail-loading">불러오는 중...</p>
              ) : !detail ? (
                <p className="detail-loading">상세 정보를 불러올 수 없습니다.</p>
              ) : (
                <dl className="detail-list">
                  <div><dt>접속 시각</dt><dd>{formatDateTime(detail.createdAt)}</dd></div>
                  <div><dt>사용자</dt><dd>{detail.adminEmail || '(미인증)'} {detail.adminId ? `(#${detail.adminId})` : ''}</dd></div>
                  <div><dt>메서드</dt><dd><span className={methodBadgeClass(detail.method)}>{detail.method}</span></dd></div>
                  <div><dt>엔드포인트</dt><dd className="mono break-all">{detail.endpoint}</dd></div>
                  <div><dt>응답 코드</dt><dd>
                    <span className={`badge ${detail.success ? 'badge-success' : 'badge-failed'}`}>
                      {detail.responseStatus ?? '-'}
                    </span>
                  </dd></div>
                  <div><dt>처리 시간</dt><dd>{detail.durationMs ?? 0} ms</dd></div>
                  <div><dt>요청 IP</dt><dd className="mono">{detail.requestIp || '-'}</dd></div>
                  <div><dt>User-Agent</dt><dd className="mono break-all small">{detail.userAgent || '-'}</dd></div>
                </dl>
              )}
            </div>
          </div>
        </div>
      )}

      <Snackbar message={snackbar.message} type={snackbar.type} isOpen={snackbar.isOpen} onClose={hideSnackbar} />
    </div>
  );
};

export default SystemManagement;